import React, { useEffect, useMemo, useState } from 'react';

import {
  UseFormWatch,
  UseFormSetValue,
  UseFormRegister,
  Control,
  FieldErrors,
  UseFormReset,
  UseFieldArrayAppend,
  FieldArrayWithId,
  Path,
  UseFieldArrayRemove,
  PathValue,
} from 'react-hook-form';

import {
  UPLOAD_DOCUMENTS_ALLOWED_FILE_TYPES,
  UPLOAD_DOCUMENTS_ALLOWED_FILE_TYPES_TEXT,
  UPLOAD_DOCUMENTS_DRAG_HEAD_TITLE,
  UPLOAD_DOCUMENTS_MAX_FILE_SIZE,
} from 'constants/documents';
import { ExternalShareExchangeFieldNames } from 'constants/external-share-exchange';
import { FormItem, Option } from 'interfaces';
import { ExternalShareExchangeWatchValues, FileUploadEntry } from 'interfaces/external-share-exchange.interface';
import {
  OverwrittenShareExchangeFormValues,
  OverwrittenShareExchangeItem,
  OverwrittenShareExchangesValues,
} from 'interfaces/overwritten-share-exchange.interfaces';
import { getDocumentTypeOptions } from 'modules/documents/action';
import { selectDocumentTypeOptions } from 'modules/documents/selectors';
import { useAppDispatch, useAppSelector } from 'modules/store';
import StartInvestmentShareExchangeAddNewRow from 'page-components/my-investments/start-investment-share-exchange-modal/StartInvestmentShareExchangeAddNewRow';
import StartInvestmentShareExchangeModalTableItem from 'page-components/my-investments/start-investment-share-exchange-modal/StartInvestmentShareExchangeModalTableItem';
import { FormContent, SnippetLayout } from 'shared-components';

import AddExternalTotalShareExchangeList from '../share-exchanges-extarnals/AddExternalTotalShareExchangeList';
import UploadFileWithDocType from './UploadFileWithDocType';

interface OverrideShareExchangeCommonFieldsProps {
  getFields: (
    watchValues: ExternalShareExchangeWatchValues,
    classOfSharesOptions: Option[],
    dealIndex: number,
  ) => FormItem[];
  dealsFields: FieldArrayWithId<OverwrittenShareExchangeFormValues, 'exchanges', 'id'>[];
  shareExchanges: OverwrittenShareExchangeItem[];
  register: UseFormRegister<OverwrittenShareExchangeFormValues>;
  control: Control<OverwrittenShareExchangeFormValues>;
  watch: UseFormWatch<OverwrittenShareExchangeFormValues>;
  errors: FieldErrors<OverwrittenShareExchangeFormValues>;
  setValue: UseFormSetValue<OverwrittenShareExchangeFormValues>;
  reset: UseFormReset<OverwrittenShareExchangeFormValues>;
  append: UseFieldArrayAppend<OverwrittenShareExchangesValues>;
  remove: UseFieldArrayRemove;
  isFormDisabled?: boolean;
  shareClasses?: string[];
  companyName: string;
  initialFileValues: Partial<{ files: FileUploadEntry[] }>;
}

const OverrideShareExchangeCommonFields = ({
  getFields,
  dealsFields,
  shareExchanges,
  register,
  control,
  watch,
  errors,
  setValue,
  append,
  remove,
  isFormDisabled,
  shareClasses,
  companyName,
  reset,
}: OverrideShareExchangeCommonFieldsProps) => {
  const dispatch = useAppDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const documentTypes = useAppSelector(selectDocumentTypeOptions);

  const classOfSharesOptions: Option[] = useMemo(() => {
    return shareClasses
      ? shareClasses.map((classOfShares) => ({
          label: classOfShares,
          value: classOfShares,
        }))
      : [];
  }, [shareClasses]);

  useEffect(() => {
    setIsLoading(true);
    dispatch(getDocumentTypeOptions())
      .unwrap()
      .finally(() => setIsLoading(false));
  }, []);

  const documentTypeOptions = useMemo(() => {
    return documentTypes.map((documentType) => ({
      label: documentType.name,
      value: documentType.value,
    }));
  }, [documentTypes]);

  const currentClosingDate = String(watch('exchanges.0.closingDate' as Path<OverwrittenShareExchangeFormValues>) || '');

  useEffect(() => {
    if (currentClosingDate) {
      dealsFields.forEach((_, index) => {
        setValue(
          `exchanges.${index}.closingDate` as Path<OverwrittenShareExchangeFormValues>,
          currentClosingDate as unknown as PathValue<
            OverwrittenShareExchangeFormValues,
            Path<OverwrittenShareExchangeFormValues>
          >,
        );
      });
    }
  }, [currentClosingDate, setValue, dealsFields]);

  const appendNewInvestmentsRow = () => {
    append({
      classOfShares: '',
      companyPrice: '',
      fundSharePrice: '',
      noExchangedShares: '',
      noOfShares: '',
      closingDate: currentClosingDate,
      originalInvestmentPrice: '',
    });
  };

  useEffect(() => {
    const firstExchangePrefix = `exchanges.0`;

    if (classOfSharesOptions?.length === 1) {
      const { value } = classOfSharesOptions[0];
      const optionToSet: Option = { label: value, value };

      setValue(
        `${firstExchangePrefix}.${ExternalShareExchangeFieldNames.CLASS_OF_SHARES}` as Path<OverwrittenShareExchangeFormValues>,
        optionToSet as PathValue<OverwrittenShareExchangeFormValues, Path<OverwrittenShareExchangeFormValues>>,
      );
    }
  }, [setValue, shareExchanges]);

  return (
    <>
      <SnippetLayout
        title={companyName}
        description='Please confirm the class of shares and number of shares you’d like to exchange'
        className='mb-10 mt-6 [&>div_p]:pb-2'
        childrenClassName='mt-0'
      >
        <div className='flex flex-col gap-2'>
          {dealsFields?.map((deal, dealIndex) => {
            const dealsPrefix: `exchanges.${number}` = `exchanges.${dealIndex}`;

            const fundSharePrice = watch(
              `${dealsPrefix}.${ExternalShareExchangeFieldNames.DAXIA_PRICE}` as Path<OverwrittenShareExchangeFormValues>,
            );

            const noOfShares = watch(
              `${dealsPrefix}.${ExternalShareExchangeFieldNames.NO_OF_SHARES}` as Path<OverwrittenShareExchangeFormValues>,
            );
            const watchValues = {
              noOfShares,
              fundSharePrice,
              companyPrice:
                watch(
                  `${dealsPrefix}.${ExternalShareExchangeFieldNames.COMPANY_PRICE}` as Path<OverwrittenShareExchangeFormValues>,
                ) || '0',
            };

            const watchClassOfShares = watch(
              `${dealsPrefix}.${ExternalShareExchangeFieldNames.CLASS_OF_SHARES}` as Path<OverwrittenShareExchangeFormValues>,
            ) as Option;

            return (
              <StartInvestmentShareExchangeModalTableItem
                key={deal.id}
                isOnlyOne={dealsFields?.length === 1}
                isLast={dealIndex === dealsFields?.length - 1}
                handleRemoveRow={() => remove(dealIndex)}
                watchClassOfSharesValue={watchClassOfShares?.value}
              >
                <FormContent
                  isDisabled={isFormDisabled}
                  fields={getFields(watchValues as ExternalShareExchangeWatchValues, classOfSharesOptions, dealIndex)}
                  register={register}
                  control={control}
                  errors={errors}
                  anchorPrefix='start-investment-share-exchange-modal'
                />
              </StartInvestmentShareExchangeModalTableItem>
            );
          })}

          <StartInvestmentShareExchangeAddNewRow addNewRow={appendNewInvestmentsRow} />
        </div>
      </SnippetLayout>
      <UploadFileWithDocType
        allowedFileHelpText={UPLOAD_DOCUMENTS_ALLOWED_FILE_TYPES_TEXT}
        dragAreaTitle={UPLOAD_DOCUMENTS_DRAG_HEAD_TITLE}
        allowedFileTypes={UPLOAD_DOCUMENTS_ALLOWED_FILE_TYPES}
        maxFileSize={UPLOAD_DOCUMENTS_MAX_FILE_SIZE}
        reset={reset}
        documentTypeOptions={documentTypeOptions}
        control={control as any}
        watchFieldNamePrefix='files'
        setValue={setValue as unknown as UseFormSetValue<OverwrittenShareExchangeFormValues>}
        watch={watch as unknown as UseFormWatch<OverwrittenShareExchangeFormValues>}
      />

      <AddExternalTotalShareExchangeList shareExchanges={shareExchanges as OverwrittenShareExchangeItem[] as any} />
    </>
  );
};

export default OverrideShareExchangeCommonFields;
